import React, { useState, useMemo } from "react";
import classNames from "classnames";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Label,
    Table,
} from "reactstrap";
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import {
    getTheme2FormFieldsCss,
    getTheme2SearchFilterSingleSelectStyles,
    THEME2_SEARCH_FILTER_FIELD_WIDTH,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useTranslation } from "react-i18next";
import { useGenerateDailyReport } from "api/reports/service";
import {
    Button,
    Spinner,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useNavigate } from "react-router-dom";



const DailyRecap = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const isTheme2 = shellThemeVariant === "2";
    const filterSelectStyles = useMemo(
        () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? `${getTheme2FormFieldsCss({
                      scopeSelector: ".daily-recap-page.daily-recap-page--theme2",
                      fieldsSelector: ".daily-recap-filters",
                  })}
                  .daily-recap-page.daily-recap-page--theme2 table th,
                  .daily-recap-page.daily-recap-page--theme2 table td {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .daily-recap-page.daily-recap-page--theme2 .nav-link {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }`
                : "",
        [isTheme2]
    );

    const [operator, setOperator] = useState({ value: "dynamic", label: "Dynamic" });
    const [value, setValue] = useState({ value: "34", label: "Today" });

    const [reportResult, setReportResult] = useState(null);
    const [showViewer, setShowViewer] = useState(false);
    const [activeTab, setActiveTab] = useState("advanced");
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);


    const generateMutation = useGenerateDailyReport();

    const handleGenerate = async () => {
        try {
            const result = await generateMutation.mutateAsync({
                operator: operator.value,
                value: value.value
            });
            setReportResult(result);
            setShowViewer(true);
        } catch (error) {
            console.error("Error generating report:", error);
        }
    };

    const handleBack = () => {
        setShowViewer(false);
    };

    const handleExport = async (format) => {
        setIsDownloading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}reports/daily-report?format=${format}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    data: {
                        operator: operator.value,
                        value: value.value,
                        tab: activeTab
                    }
                })
            });

            if (!response.ok) throw new Error('Export failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const randomName = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            a.download = `${randomName}.${format === 'excel' ? 'xlsx' : format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export error:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const gridData = useMemo(() => {
        if (!reportResult) return [];
        if (Array.isArray(reportResult)) return reportResult;
        return reportResult.results || reportResult.data || [];
    }, [reportResult]);

    const combinedHtml = useMemo(() => {
        if (!reportResult) return "";
        if (typeof reportResult === 'string') return reportResult;

        const { detail, summary } = reportResult;
        if (!detail && !summary) return "";

        // If we have both, combine them. If only one, show that.
        if (detail && summary) {
            return `
                <html>
                    <head>
                        <style>
                            body { font-family: 'Inter', sans-serif; padding: 20px; }
                            .summary-section { margin-bottom: 40px; border-bottom: 2px solid #eee; padding-bottom: 20px; }
                        </style>
                    </head>
                    <body>
                        <div class="summary-section">
                            ${summary}
                        </div>
                        <div class="detail-section">
                            ${detail}
                        </div>
                    </body>
                </html>
            `;
        }
        return detail || summary || "";
    }, [reportResult]);

    const gridColumns = useMemo(() => {
        if (gridData.length === 0) return [];
        return Object.keys(gridData[0]).map(key => ({
            Header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
            accessor: key
        }));
    }, [gridData]);



    const operatorOptions = [
        { value: ">", label: "Greater Than" },
        { value: "<", label: "Less Than" },
        { value: ">=", label: "Greater Than or Equal" },
        { value: "<=", label: "Less Than or Equal" },
        { value: "between", label: "Between" },
        { value: "dynamic", label: "Dynamic" },
    ];


    const valueOptions = [
        { value: "115", label: "Last 30 Days" },
        { value: "5", label: "Last 7 Days" },
        { value: "47", label: "Last Month" },
        { value: "48", label: "Last Month to Date" },
        { value: "49", label: "Last Quarter" },
        { value: "50", label: "Last Quarter to Date" },
        { value: "41", label: "Last Week" },
        { value: "46", label: "Last Week to Date" },
        { value: "51", label: "Last Year" },
        { value: "52", label: "Last Year to Date" },
        { value: "54", label: "Next 4 Weeks" },
        { value: "55", label: "Next Month" },
        { value: "56", label: "Next Quarter" },
        { value: "53", label: "Next Week" },
        { value: "57", label: "Next Year" },
        { value: "145", label: "Prior to Today" },
        { value: "40", label: "This Month" },
        { value: "33", label: "This Month to Date" },
        { value: "42", label: "This Quarter" },
        { value: "43", label: "This Quarter to Date" },
        { value: "38", label: "This Week" },
        { value: "39", label: "This Week to Date" },
        { value: "44", label: "This Year" },
        { value: "45", label: "This Year to Date" },
        { value: "34", label: "Today" },
        { value: "35", label: "Yesterday" },
    ];


    return (
        <div
            className={classNames("content daily-recap-page", {
                "daily-recap-page--theme2": isTheme2,
                "theme2-admin-content-compact": isTheme2,
                "theme2-daily-recap-inset": isTheme2,
            })}
        >
            {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
            <style>
                {`
          ${isTheme2 ? `
          .daily-recap-page.daily-recap-page--theme2 .daily-recap-filter-field {
            width: ${THEME2_SEARCH_FILTER_FIELD_WIDTH};
            max-width: 100%;
          }
          ` : ""}
          .daily-recap-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .daily-recap-card-header h2,
          .daily-recap-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .daily-recap-page .align-items-center {
            margin-top: 3px;
          }
          .daily-recap-page .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
        `}
            </style>
            {!showViewer ? (
                <Row className="mt-0">
                    <Col md="12">
                        <Card>
                            <CardHeader className="daily-recap-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h2 className="mb-0">{t('routes.dailyRecap')}</h2>
                                    <Button
                                        size="sm"
                                        style={{ ...activeColorStyle, color: 'white' }}
                                        onClick={handleGenerate}
                                        disabled={generateMutation.isLoading}
                                    >
                                        {generateMutation.isLoading ? (
                                            <>
                                                <Spinner size="sm" className="mr-2" />
                                                {t('reports.generating') || "Generating..."}
                                            </>
                                        ) : (
                                            t('reports.generate') || "Generate"
                                        )}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className={isTheme2 ? "daily-recap-filters" : undefined}>
                                <Table responsive borderless style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                                            <th style={{ color: '#999' }}>Field Name</th>
                                            <th style={{ color: '#999' }}>Operator</th>
                                            <th style={{ color: '#999' }}>Value(s)</th>
                                            <th style={{ color: '#999' }}>DataSource Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ verticalAlign: 'middle', fontWeight: 700, }}>PostDate</td>
                                            <td style={{ verticalAlign: 'middle', minWidth: isTheme2 ? undefined : '200px' }}>
                                                <div className={isTheme2 ? "daily-recap-filter-field" : undefined}>
                                                <Select
                                                    activeColor={activeColor}
                                                    styles={filterSelectStyles}
                                                    options={operatorOptions}
                                                    value={operator}
                                                    onChange={(opt) => setOperator(opt)}
                                                />
                                                </div>
                                            </td>
                                            <td style={{ verticalAlign: 'middle', minWidth: isTheme2 ? undefined : '200px' }}>
                                                <div className={isTheme2 ? "daily-recap-filter-field" : undefined}>
                                                <Select
                                                    activeColor={activeColor}
                                                    styles={filterSelectStyles}
                                                    options={valueOptions}
                                                    value={value}
                                                    onChange={(opt) => setValue(opt)}
                                                />
                                                </div>
                                            </td>
                                            <td style={{ verticalAlign: 'middle', fontWeight: 700, }}>Transactions</td>
                                        </tr>
                                    </tbody>
                                </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            ) : (
                <Row className="mt-0">
                    <Col md="12">
                        <Card >
                            <CardHeader className="daily-recap-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <h2 className="mb-0">{t('reports.reportViewer') || "Report Viewer"}</h2>
                                    </div>
                                    <div className="d-flex">
                                        <Button
                                            color="secondary"
                                            onClick={handleBack}
                                            className="btn-round mr-2"
                                            size="sm"
                                        >
                                            <i className="tim-icons icon-minimal-left mr-1" />
                                            {t('common.back') || "BACK"}
                                        </Button>
                                        <Dropdown isOpen={exportDropdownOpen} toggle={() => !isDownloading && setExportDropdownOpen(!exportDropdownOpen)}>
                                            <DropdownToggle
                                                caret
                                                color="primary"
                                                style={activeColorStyle}
                                                className="btn-round"
                                                size="sm"
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <>
                                                        <Spinner size="sm" className="mr-2" />
                                                        {t('common.loading') || "Loading..."}
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="tim-icons icon-cloud-download-93 mr-1" />
                                                        {t('reports.viewer.export') || "EXPORT"}
                                                    </>
                                                )}
                                            </DropdownToggle>
                                            <DropdownMenu right>
                                                <DropdownItem onClick={() => handleExport('pdf')}>
                                                    <i className="fas fa-file-pdf mr-2 text-danger" />
                                                    PDF
                                                </DropdownItem>
                                                <DropdownItem onClick={() => handleExport('excel')}>
                                                    <i className="fas fa-file-excel mr-2 text-success" />
                                                    Excel
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </div>
                                </div>
                            </CardHeader>

                        </Card>
                        <Nav tabs
                            className="border-0 flex-nowrap"
                            style={{ borderBottom: "none", width: 'max-content', minWidth: '100%' }}
                        >
                            <NavItem>
                                <NavLink
                                    className={activeTab === "advanced" ? "active font-weight-bold" : "text-muted"}
                                    onClick={() => setActiveTab("advanced")}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        position: "relative",
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        color: activeTab === "advanced" ? activeColorStyle.borderColor : undefined,
                                        fontWeight: activeTab === "advanced" ? 700 : 500
                                    }}                                    >
                                    {t('reports.advancedViewer')}
                                    {activeTab === "advanced" && (
                                        <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem className="ml-2">
                                <NavLink
                                    className={activeTab === "grid" ? "active font-weight-bold" : "text-muted"}
                                    onClick={() => setActiveTab("grid")}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        position: "relative",
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        color: activeTab === "grid" ? activeColorStyle.borderColor : undefined,
                                        fontWeight: activeTab === "grid" ? 700 : 500
                                    }}
                                >
                                    {t('reports.gridViewer')}
                                    {activeTab === "grid" && (
                                        <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                                    )}
                                </NavLink>
                            </NavItem>
                        </Nav>
                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="advanced">
                                {reportResult?.summary ? (
                                    <iframe
                                        srcDoc={reportResult.summary}
                                        style={{
                                            width: '100%',
                                            height: '75vh',
                                            border: 'none'
                                        }}
                                        title="Daily Report Summary"
                                    />
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="tim-icons icon-chart-bar-32" style={{ fontSize: '4rem', color: '#6c757d' }} />
                                        <h4 className="mt-3 text-muted">{t('reports.viewer.noSummaryData') || "Summary not available"}</h4>
                                    </div>
                                )}
                            </TabPane>
                            <TabPane tabId="grid">
                                {reportResult?.detail ? (
                                    <iframe
                                        srcDoc={reportResult.detail}
                                        style={{
                                            width: '100%',
                                            height: '75vh',
                                            border: 'none'
                                        }}
                                        title="Daily Report Detail"
                                    />
                                ) : (
                                    <div className="text-center py-5 text-muted">
                                        {t('common.noData') || "Detail not available"}
                                    </div>
                                )}
                            </TabPane>

                        </TabContent>
                    </Col>
                </Row>
            )}
        </div>
    );
};



export default DailyRecap;

